// Profile-photo upload. Kept deliberately self-contained and dependency-free so a
// player can ALWAYS set a photo: we use a plain <input type="file"> (which on iOS
// surfaces the native camera / photo-library sheet) and the browser's own canvas
// to normalise whatever they pick into a small, square JPEG before upload. That
// means uploads stay tiny (well under the 5 MB Storage-rule cap), render fast
// everywhere, and display consistently — no Capacitor Camera plugin required.
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/** Square output edge. A crisp avatar at every size the app renders (≤ 84px). */
const OUTPUT_SIZE = 512;
const JPEG_QUALITY = 0.85;
/** Mirrors the Storage rule (config/storage.rules): images only, max 5 MB. */
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Fixed per-user path (matches config/storage.rules `profiles/{uid}/profile.jpg`). */
const avatarPath = (uid: string): string => `profiles/${uid}/profile.jpg`;

/** A friendly, user-surfaceable upload failure. */
export class AvatarUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AvatarUploadError';
  }
}

interface Decoded {
  width: number;
  height: number;
  source: CanvasImageSource;
  release: () => void;
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = url;
  });
}

/** Decode a picked file into something canvas can draw, preferring the fast,
 *  broadly-compatible `createImageBitmap` (which also decodes HEIC on modern
 *  Safari) and falling back to an <img> element. */
async function decodeImage(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      // `from-image` honours EXIF orientation, so portrait photos from an iPhone
      // camera aren't rotated sideways. (The <img> fallback path applies EXIF
      // automatically.) The cast keeps older TS DOM libs that omit the option happy.
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { width: bmp.width, height: bmp.height, source: bmp, release: () => bmp.close() };
    } catch {
      // Fall through to the <img> path below.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(url);
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      source: img,
      release: () => URL.revokeObjectURL(url)
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Image export failed'))), type, quality);
  });
}

/**
 * Centre-crop to a square and downscale to {@link OUTPUT_SIZE}, exported as JPEG.
 * Never upscales below the source (a tiny image stays sharp at its own size).
 */
export async function processAvatarImage(file: Blob): Promise<Blob> {
  const decoded = await decodeImage(file);
  try {
    if (!decoded.width || !decoded.height) throw new Error('Empty image');
    const side = Math.min(decoded.width, decoded.height);
    const sx = (decoded.width - side) / 2;
    const sy = (decoded.height - side) / 2;
    const target = Math.min(OUTPUT_SIZE, side);

    const canvas = document.createElement('canvas');
    canvas.width = target;
    canvas.height = target;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, sx, sy, side, side, 0, 0, target, target);

    return await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  } finally {
    decoded.release();
  }
}

/**
 * Upload the player's chosen image as their profile photo and return its download
 * URL. The image is normalised to a small square JPEG first; if that conversion
 * can't run (an exotic format on an old browser), the original is uploaded as long
 * as it's a reasonably-sized image — so the player can always set *a* photo.
 *
 * Overwriting the fixed `profile.jpg` path mints a new download token, so the
 * returned URL always points at the freshly-uploaded image (no stale cache).
 */
export async function uploadAvatar(uid: string, file: File | Blob): Promise<string> {
  if (!uid) throw new AvatarUploadError('You need to be signed in to set a photo.');

  const sourceType = file.type || '';
  if (sourceType && !sourceType.startsWith('image/')) {
    throw new AvatarUploadError('Please choose an image file.');
  }

  let blob: Blob;
  let contentType = 'image/jpeg';
  try {
    blob = await processAvatarImage(file);
  } catch {
    // Couldn't decode/convert — fall back to the original bytes if they're a
    // valid, within-limits image; otherwise surface a clear, actionable message.
    if (!sourceType.startsWith('image/')) {
      throw new AvatarUploadError('That image format isn’t supported. Try a JPG or PNG.');
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new AvatarUploadError('That image is too large — please pick one under 5 MB.');
    }
    blob = file;
    contentType = sourceType;
  }

  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new AvatarUploadError('That image is too large — please pick one under 5 MB.');
  }

  try {
    const storageRef = ref(storage, avatarPath(uid));
    await uploadBytes(storageRef, blob, {
      contentType,
      cacheControl: 'public, max-age=604800'
    });
    return await getDownloadURL(storageRef);
  } catch {
    throw new AvatarUploadError('Couldn’t upload your photo. Please check your connection and try again.');
  }
}

/** Best-effort removal of the stored photo object (used when clearing a photo). */
export async function deleteAvatarObject(uid: string): Promise<void> {
  if (!uid) return;
  try {
    await deleteObject(ref(storage, avatarPath(uid)));
  } catch {
    // Already gone (or never existed) — clearing the doc field is what matters.
  }
}
